import { type ActionStats, type ActionType, type DailyActionStats, type StatsDurations } from '@soulhx/fs-common';
import { useAsyncLiveQuery } from './useAsyncLiveQuery.js';
import { DateTime } from 'luxon';
import { db } from '../model/db.js';
import { getActionEntriesFromDexie } from '../helpers/action-entries.js';
import { useSpecialActionTypes } from './useSpecialActionTypes.js';

/**
 * Get `DateTime` for **timeframe** helper
 *
 * @param tf Timeframe (`StatsDurations`)
 * @returns `DateTime` obj
 */
export const getDtForTf = (tf?: StatsDurations) => {
  switch (tf) {
    case 'week': {
      return DateTime.now().minus({ weeks: 1 });
    }
    case '2weeks': {
      return DateTime.now().minus({ weeks: 2 });
    }
    case 'month': {
      return DateTime.now().minus({ months: 1 });
    }
    case 'year': {
      return DateTime.now().minus({ years: 1 });
    }
    default: {
      return DateTime.fromISO('1900-01-01');
    }
  }
};

export const useGetAllStats = (timeframe: StatsDurations): { stats: ActionStats | null; isLoading: boolean } => {
  const { data: specialTypeIds, isLoading: specialTypesLoading } = useSpecialActionTypes();

  const { data, isLoading } = useAsyncLiveQuery<ActionStats>(async () => {
    const today = DateTime.now().toISO();
    const earliestDate = getDtForTf(timeframe).toISO()!;

    const entries = await getActionEntriesFromDexie(earliestDate, today);
    const types = await db.actionTypes.toArray();

    const countsByType = new Map<string, number>();
    for (const e of entries) {
      countsByType.set(e.actionType, (countsByType.get(e.actionType) ?? 0) + 1);
    }

    const actionsArray: { action: ActionType; count: number }[] = types.map((type) => ({
      action: type,
      count: countsByType.get(type.id) ?? 0,
    }));

    const scriptureCount =
      (countsByType.get(specialTypeIds.longNT) ?? 0) +
      (countsByType.get(specialTypeIds.longOT) ?? 0) +
      (countsByType.get(specialTypeIds.shortNT) ?? 0) +
      (countsByType.get(specialTypeIds.shortOT) ?? 0);
    if (scriptureCount > 0) {
      actionsArray.push({
        action: {
          id: specialTypeIds.scripture,
          displayName: 'scripture',
          isDefault: true,
        },
        count: scriptureCount,
      });
    }

    const responseObj: ActionStats = {
      fromDate: earliestDate,
      toDate: today,
      actions: actionsArray,
    };

    return responseObj;
  }, [timeframe, specialTypeIds]);

  return { stats: data ?? null, isLoading: isLoading || specialTypesLoading };
};

export const useGetAllStatsByDay = (
  timeframe: StatsDurations
): { stats: DailyActionStats[] | null; isLoading: boolean } => {
  const { data: specialTypeIds, isLoading: specialTypesLoading } = useSpecialActionTypes();

  const { data, isLoading } = useAsyncLiveQuery<DailyActionStats[]>(async () => {
    const today = DateTime.now();
    const earliestEntry = await db.actionEntries.orderBy('entryDate').first();
    const earliestDbDate = DateTime.fromISO(earliestEntry?.entryDate || today.toISO());
    const earliestQueryDate = getDtForTf(timeframe).startOf('day');
    const queryFromDate = DateTime.max(earliestQueryDate, earliestDbDate).startOf('day');

    const dayBuckets = new Map<string, Map<string, DailyActionStats['actions'][number]>>();
    let currentDay = queryFromDate;
    while (currentDay <= today) {
      dayBuckets.set(currentDay.toISODate()!, new Map());
      currentDay = currentDay.plus({ day: 1 });
    }

    const entries = await getActionEntriesFromDexie(queryFromDate.toISO()!, today.toISO()!);
    for (const entry of entries) {
      const dayActions = dayBuckets.get(DateTime.fromISO(entry.entryDate).toISODate()!);
      if (!dayActions) continue; // should never happen but makes the TS easier

      const existing = dayActions.get(entry.actionType);
      if (existing) {
        existing.count++;
      } else {
        dayActions.set(entry.actionType, {
          count: 1,
          action: { id: entry.actionType, displayName: entry.displayName, isDefault: false },
        });
      }

      if (
        entry.actionType === specialTypeIds.longNT ||
        entry.actionType === specialTypeIds.longOT ||
        entry.actionType === specialTypeIds.shortNT ||
        entry.actionType === specialTypeIds.shortOT
      ) {
        const scripture = dayActions.get(specialTypeIds.scripture);
        if (scripture) {
          scripture.count++;
        } else {
          dayActions.set(specialTypeIds.scripture, {
            count: 1,
            action: { id: specialTypeIds.scripture, isDefault: true, displayName: 'scripture' },
          });
        }
      }
    }

    return Array.from(dayBuckets, ([date, actions]) => ({ date, actions: Array.from(actions.values()) }));
  }, [timeframe, specialTypeIds]);

  return { stats: data ?? null, isLoading: isLoading || specialTypesLoading };
};
